import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { SERVICES } from '../data/services';
import { IsoArt, ART } from './IsoArt';

const EMAIL = 'contact@qyntix.com';

/* Referencia del encargo: seis caracteres sin ambigüedades (sin 0/O, 1/I).
   Viaja en el email para poder citarla. */
function makeRef() {
  const abc = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const pick = () => abc[Math.floor(Math.random() * abc.length)];
  return `${pick()}${pick()}${pick()}-${pick()}${pick()}${pick()}`;
}

const COPY = {
  es: {
    heading: 'Cuéntanos el encargo',
    title: 'Hoja de encargo',
    ref: 'Ref.',
    intro: 'Escríbelo como se lo contarías a alguien de tu equipo. Contestamos en menos de 24 horas laborables.',
    alt: 'O escríbenos a',
    name: 'Nombre',
    email: 'Email',
    company: 'Empresa',
    optional: '(opcional)',
    service: 'Servicio',
    unsure: 'Todavía no lo sé',
    message: 'El encargo',
    placeholder: 'Por ejemplo: llevamos los expedientes en un Excel compartido y cada mes se nos escapa algún plazo.',
    submit: 'Enviar encargo',
    sending: 'Enviando…',
    privacy: 'Usamos estos datos solo para contestarte.',
    privacyLink: 'Política de privacidad',
    copy: 'Copia del encargo',
    done: `Recibido. Te contestamos desde ${EMAIL} en menos de 24 horas laborables. Si nos escribes antes, cita la referencia.`,
    errNetwork: `No se ha podido enviar. Comprueba la conexión y vuelve a probar, o escríbenos a ${EMAIL}.`,
    errServer: `El fallo es nuestro: el formulario no ha enviado nada. Escríbenos a ${EMAIL} y te contestamos en el mismo plazo.`,
    dateLocale: 'es-ES',
  },
  en: {
    heading: 'Tell us about yours',
    title: 'Job sheet',
    ref: 'Ref.',
    intro: 'We reply within one business day.',
    alt: 'Or by email:',
    name: 'Name',
    email: 'Email',
    company: 'Company',
    optional: '(optional)',
    service: 'Job',
    unsure: "Not sure yet",
    message: 'What do you do by hand today?',
    placeholder: 'For example: every Monday we copy campaign data by hand into a spreadsheet for the client.',
    submit: 'Send job sheet',
    sending: 'Sending…',
    privacy: 'We only use these details to reply to you.',
    privacyLink: 'Privacy policy',
    copy: 'Copy of your job sheet',
    done: `Received. We'll reply from ${EMAIL} within one business day. If you contact us before then, quote the reference.`,
    errNetwork: `It couldn't be sent. Check your connection and try again, or email ${EMAIL}.`,
    errServer: `The form failed on our side. Email ${EMAIL} and we'll reply just as fast.`,
    dateLocale: 'en-GB',
  },
};

export default function Contact() {
  const { lang } = useTranslation();
  const c = COPY[lang] || COPY.es;
  const ref = useMemo(makeRef, []);
  const today = new Date().toLocaleDateString(c.dateLocale, { day: 'numeric', month: 'long', year: 'numeric' });
  const [form, setForm] = useState({ name: '', email: '', company: '', service: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    const onRequest = (e) => setForm((f) => ({ ...f, service: e.detail }));
    window.addEventListener('qx:request', onRequest);
    return () => window.removeEventListener('qx:request', onRequest);
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');
    try {
      const res = await fetch('https://formspree.io/f/meeryjvl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...form, referencia: ref, lang, _subject: `Encargo ${ref}` }),
      });
      if (res.ok) setStatus('sent');
      else { setStatus('error'); setError(c.errServer); }
    } catch {
      setStatus('error');
      setError(c.errNetwork);
    }
  };

  const sending = status === 'sending';
  const serviceName = (id) => {
    const s = SERVICES.find((x) => x.id === id);
    return s ? `${s.id} ${(s[lang] || s.es).name}` : c.unsure;
  };

  return (
    <section id="contacto" className="contact" aria-labelledby="contacto-title">
      <div className="wrap contact-grid">
        <div className="contact-side">
          <h2 id="contacto-title" className="t-1">{c.heading}</h2>
          <p className="t-lead">{c.intro}</p>
          <p className="mail">{c.alt} <a href={`mailto:${EMAIL}`}>{EMAIL}</a></p>
          <div className="contact-art"><IsoArt {...ART.contact} s={22} /></div>
        </div>

        <div className="sheet">
          <header className="sheet-head">
            <h3 className="t-3">{status === 'sent' ? c.copy : c.title}</h3>
            <p className="sheet-ref">
              <span>{c.ref} <b>{ref}</b></span>
              <span>{today}</span>
            </p>
          </header>

          {status === 'sent' ? (
            <div className="sheet-copy" role="status">
              <dl>
                <dt>{c.name}</dt><dd>{form.name}</dd>
                {form.company && (<><dt>{c.company}</dt><dd>{form.company}</dd></>)}
                <dt>{c.service}</dt><dd>{serviceName(form.service)}</dd>
                <dt>{c.message}</dt><dd>{form.message}</dd>
              </dl>
              <p className="t-lead">{c.done}</p>
            </div>
          ) : (
            <form className="form" onSubmit={onSubmit}>
              <div className="form-row">
                <div className="field">
                  <label htmlFor="name">{c.name}</label>
                  <input className="input" id="name" name="name" autoComplete="name" required value={form.name} onChange={onChange} />
                </div>
                <div className="field">
                  <label htmlFor="email">{c.email}</label>
                  <input className="input" id="email" name="email" type="email" autoComplete="email" required value={form.email} onChange={onChange} />
                </div>
              </div>
              <div className="form-row">
                <div className="field">
                  <label htmlFor="company">{c.company} <span className="opt">{c.optional}</span></label>
                  <input className="input" id="company" name="company" autoComplete="organization" value={form.company} onChange={onChange} />
                </div>
                <div className="field">
                  <label htmlFor="service">{c.service}</label>
                  <select className="input" id="service" name="service" value={form.service} onChange={onChange}>
                    <option value="">{c.unsure}</option>
                    {SERVICES.map((s) => <option key={s.id} value={s.id}>{s.id} · {(s[lang] || s.es).name}</option>)}
                  </select>
                </div>
              </div>
              <div className="field">
                <label htmlFor="message">{c.message}</label>
                <textarea className="input" id="message" name="message" rows={4} required placeholder={c.placeholder} value={form.message} onChange={onChange} />
              </div>
              {error && <p className="form-error" role="alert">{error}</p>}
              <div className="sheet-foot">
                <button type="submit" className="btn" disabled={sending} aria-busy={sending}>
                  {sending ? c.sending : c.submit}
                </button>
                <p className="t-small">{c.privacy} <Link to="/privacidad">{c.privacyLink}</Link></p>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
